import { Home } from '@mui/icons-material';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useWSContext } from '../contexts/WebSocketContext';
import { androidNotification } from '../controller/android-interface-controller';
import { MotionContainer, varBounceInDown } from './animate';

const TEMPO_INATIVIDADE_SEGUNDOS = 5 * 20; // 5 minutos

const DESATIVAR_INATIVIDADE = process.env.REACT_APP_DISABLE_INATIVIDADE === 'true';

function ChatInatividade() {
  const {
    ws,
    storedMessages,
    supportLeftMessage,
    pararTimerAtivado,
    setPararTimerAtivado,
    handleInactivityEndChat,
  } = useWSContext();

  const [maxTempInatividade, setMaxTempInatividade] = useState(
    TEMPO_INATIVIDADE_SEGUNDOS
  );
  const [modalChatEncerradoVisivel, setModalChatEncerradoVisivel] = useState(false);
  const intervalIdRef = useRef(null);
  const [progresso, setProgresso] = useState(100);

  const finalizarAtendimento = useCallback(() => {
    // console.log('ChatInatividade: Finalizando atendimento devido à inatividade.');

    if (handleInactivityEndChat) {
       handleInactivityEndChat();
    }

    setModalChatEncerradoVisivel(true);

  }, [handleInactivityEndChat]);

  const pararTimer = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setMaxTempInatividade(TEMPO_INATIVIDADE_SEGUNDOS);
    setProgresso(100);
  }, []);

  const iniciarTimer = useCallback(() => {
    if (DESATIVAR_INATIVIDADE) return;
    if (intervalIdRef.current) return;

    // console.log('🎯 Iniciando timer de resposta do cliente...');
    setMaxTempInatividade(TEMPO_INATIVIDADE_SEGUNDOS);
    setProgresso(100);

    intervalIdRef.current = setInterval(() => {
      setMaxTempInatividade((downtime) => {
        if (downtime === 60) {
          androidNotification(
            'Mensagem automática',
            'Em 60 segundos você será desconectado por inatividade!'
          );
        }

        if (downtime <= 1) {
          finalizarAtendimento();
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
          return 0;
        }

        setProgresso(((downtime - 1) * 100) / TEMPO_INATIVIDADE_SEGUNDOS);
        return downtime - 1;
      });
    }, 1000);
  }, [finalizarAtendimento]);

  useEffect(() => {
    if (pararTimerAtivado) {
      pararTimer();
      setPararTimerAtivado(false);
    }
  }, [pararTimerAtivado, pararTimer, setPararTimerAtivado]);

  useEffect(() => () => {
      pararTimer();
    }, [pararTimer]);

  useEffect(() => {
    if (supportLeftMessage) {
      // console.log('❌ O suporte saiu, parando o timer definitivamente.');
      pararTimer();
      return;
    }
    if (storedMessages.length === 0) {
      return;
    }

    const ultimaMensagem = storedMessages[storedMessages.length - 1];

    if (ultimaMensagem?.userType === 'cliente') {
      // console.log('👤 Mensagem do cliente. Timer PARADO. Aguardando suporte.');
      pararTimer();
    } else if (ultimaMensagem?.userType === 'suporte') {
      // console.log('👨‍💻 Mensagem do suporte. (Re)iniciando timer para o cliente.');
      pararTimer();
      setTimeout(() => {
        iniciarTimer();
      }, 10);
    }
  }, [storedMessages, supportLeftMessage, pararTimer, iniciarTimer]);

  useEffect(() => {
    if (!ws?.getWebSocket) {
      return () => {};
    }
    const interval = setInterval(() => {
      const socket = ws.getWebSocket();
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        pararTimer();
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [ws, pararTimer]);

  // ==================== Bloco JSX (Visual) Revertido ====================
  return (
    <Box>
      <MotionContainer initial="initial" open>
        <motion.div variants={varBounceInDown}>
          {maxTempInatividade <= 60 &&
            maxTempInatividade > 0 &&
            !modalChatEncerradoVisivel && (
              <Alert
                variant="filled"
                severity="warning"
                sx={{
                  color: 'white',
                  backgroundColor: '#FFA500',
                  borderRadius: '0px 0px 27px 27px',
                  boxShadow: 1,
                  p: 1,
                  m: 'auto',
                  justifyContent: 'center',
                  '& .MuiAlert-icon': {
                    display: 'none',
                  },
                }}
              >
                Encerrando chat em {maxTempInatividade} segundo
                {maxTempInatividade !== 1 && 's'} por inatividade. Para parar o
                timer, envie uma mensagem.
                <LinearProgress
                  variant="determinate"
                  value={progresso}
                  sx={{
                    marginTop: '8px',
                    height: '6px',
                    width: '100%',
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#00678D',
                    },
                  }}
                />
              </Alert>
            )}

          {modalChatEncerradoVisivel && (
            <Alert
              variant="filled"
              severity="error"
              sx={{
                borderRadius: '0px 0px 28px 28px',
                boxShadow: 1,
                p: 1.0,
                m: 'auto',
                justifyContent: 'center',
                textAlign: 'center',
                '& .MuiAlert-icon': {
                  display: 'none',
                },
              }}
            >
              <Box>
                <p>O chat foi encerrado por inatividade.</p>
                <p>Para iniciar um novo atendimento, volte à tela inicial.</p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="contained"
                  color="error"
                  startIcon={<Home />}
                  sx={{
                    marginTop: '8px',
                    padding: '3px 10px',
                    fontSize: '0.88rem',
                    borderRadius: '12px',
                    border: '1px solid white',
                    color: 'white',
                    minWidth: '100px',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }}
                >
                  Voltar
                </Button>
              </Box>
            </Alert>
          )}
        </motion.div>
      </MotionContainer>
    </Box>
  );
  // ======================================================================
}

export default ChatInatividade;
